// src/components/Header.jsx
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext.jsx";
import { fetchCities, absMediaUrl } from "../api/events.js";
import logo from "../assets/logo2.png";
import AuthModal from "../pages/AuthModal.jsx";

export default function Header() {
  const nav = useNavigate();
  const location = useLocation();
  const auth = useAuth?.() ?? {};
  const user = auth?.user ?? null;
  const logout = auth?.logout ?? (() => { });
  const role = String(user?.role || "").toLowerCase();
  const isAdmin = role === "admin" || role === "superadmin";
  const refreshUser = auth?.refreshUser ?? (() => { });

  /* ── City (dynamic) ─────────────────────────────────────────────── */
  const [, setCitiesOpt] = useState([{ name: "All", value: "all" }]);
  const [, setCitiesLoading] = useState(true);
  const [city, setCity] = useState(() => localStorage.getItem("bb_city") || "all");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setCitiesLoading(true);
        const list = await fetchCities();
        if (!alive) return;
        const withAll = [{ name: "All", value: "all" }, ...(Array.isArray(list) ? list : [])];
        setCitiesOpt(withAll);
        const exists = withAll.some((c) => String(c.value) === String(city));
        if (!exists) setCity("all");
      } catch {
        const fallback = [
          { name: "All", value: "all" },
          { name: "Mumbai", value: "Mumbai" },
          { name: "Delhi", value: "Delhi" },
          { name: "Bengaluru", value: "Bengaluru" },
          { name: "Pune", value: "Pune" },
          { name: "Hyderabad", value: "Hyderabad" },
        ];
        setCitiesOpt(fallback);
        const exists = fallback.some((c) => String(c.value) === String(city));
        if (!exists) setCity("all");
      } finally {
        setCitiesLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user?.token) {
      refreshUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    localStorage.setItem("bb_city", city);
    window.dispatchEvent(new CustomEvent("city:change", { detail: city }));
  }, [city]);

  const avatarSrc = absMediaUrl(user?.avatar || user?.profileImage) || user?.avatar || user?.profileImage || "";

  /* ── Search ─────────────────────────────────────────────────────── */
  const [q, setQ] = useState("");
  const onSearch = (e) => {
    e?.preventDefault();
    const query = q.trim();
    if (!query) return;
    nav(`/search?q=${encodeURIComponent(query)}`);
  };

  /* ── Tickets/Auth ───────────────────────────────────────────────── */
  const onTicketsClick = () => {
    if (user?.token) nav("/tickets");
    else openAuth("login", "/tickets");
  };

  // 🔸 Auth modal state
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState("login"); // 'login' | 'register'
  const [nextAfterAuth, setNextAfterAuth] = useState("/");

  const openAuth = (tab = "login", next = "/") => {
    setAuthTab(tab);
    setNextAfterAuth(next);
    setAuthOpen(true);
  };

  // If the user manually navigates to /login or /register, open modal instead
  useEffect(() => {
    const p = location.pathname.toLowerCase();
    if (p === "/login" || p === "/register") {
      const sp = new URLSearchParams(location.search);
      const next = sp.get("next") || "/";
      openAuth(p === "/login" ? "login" : "register", next);
      nav("/", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  /* ── Slide-over panel (existing) ────────────────────────────────── */
  const [panelOpen, setPanelOpen] = useState(false);
  const panelRef = useRef(null);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    function onDocClick(e) {
      if (!panelOpen) return;
      if (!panelRef.current) return;
      if (!panelRef.current.contains(e.target)) setPanelOpen(false);
    }
    function onEsc(e) {
      if (e.key === "Escape") setPanelOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [panelOpen]);

  useEffect(() => {
    if (panelOpen) setTimeout(() => closeButtonRef.current?.focus(), 80);
  }, [panelOpen]);

  const getInitials = (name = "") => {
    const parts = name.trim().split(/\s+/);
    if (!parts.length) return "U";
    if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const handleLogout = () => {
    setPanelOpen(false);
    try {
      logout();
    } catch {
      openAuth("login");
    }
  };

  /* ── Panel prefs (UI only) ──────────────────────────────────────── */

  return (
    <>
      {/* page-level animations */}
      <style>{`
        @keyframes goldShift {
          0% { background-position: 0% 50% }
          50% { background-position: 100% 50% }
          100% { background-position: 0% 50% }
        }
        .fade-in { animation: fadeIn .35s ease both; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(6px) } to { opacity: 1; transform: none } }
      `}</style>

      <header className="sticky top-0 z-50">
        {/* animated gold hairline */}
        <div
          className="h-[2px] bg-gradient-to-r from-amber-400 via-yellow-400 to-orange-400"
          style={{ backgroundSize: "200% 200%", animation: "goldShift 6s ease infinite" }}
        />
        <div className="backdrop-blur bg-white/80 border-b overflow-x-clip">
          <div className="mx-auto max-w-7xl min-w-0 px-3 sm:px-4 py-2 sm:py-2.5 grid grid-cols-12 items-center gap-2 sm:gap-3">
            {/* Logo */}
            <div className="col-span-9 md:col-span-3 min-w-0">
              <Link to="/" className="flex items-center gap-2">
                <div className="h-11 sm:h-12 w-auto max-w-[200px] sm:max-w-[260px] md:max-w-[300px] lg:max-w-[320px] rounded-lg overflow-hidden shrink-0">
                  <img src={logo} alt="Logo" className="h-full w-full max-h-12 object-contain object-left" />
                </div>
              </Link>
            </div>

            {/* MOBILE: profile / login only (nav is in MobileBottomNav) */}
            <div className="col-span-3 flex justify-end md:hidden">
              {user?.token ? (
                <button
                  type="button"
                  onClick={() => setPanelOpen(true)}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white shadow-sm"
                  aria-label="Profile menu"
                  aria-controls="user-panel"
                  aria-expanded={panelOpen}
                >
                  {avatarSrc ? (
                    <img
                      src={avatarSrc}
                      alt=""
                      className="h-8 w-8 rounded-full object-cover"
                    />
                  ) : (
                    <span className="grid h-8 w-8 place-items-center rounded-full bg-gradient-to-br from-amber-100 to-orange-200 text-xs font-bold text-amber-950">
                      {getInitials(user?.name || user?.phone || "")}
                    </span>
                  )}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() =>
                    openAuth("login", window.location.pathname + window.location.search)
                  }
                  className="rounded-full bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white"
                >
                  Log in
                </button>
              )}
            </div>

            {/* Search (desktop only) */}
            <form onSubmit={onSearch} className="hidden md:block md:col-span-5 order-last md:order-none">
              <div className="relative group">
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search events, artists, venues"
                  className="w-full rounded-lg border px-3 py-2 pl-9 text-sm text-slate-900 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-amber-500 bg-white shadow-sm"
                />
                <svg
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-amber-600"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </div>
            </form>

            {/* Right controls (desktop) */}
            <div className="col-span-12 md:col-span-4 md:flex items-center justify-end gap-2 hidden">
              {/* <button
                onClick={onTicketsClick}
                className="rounded-full border px-4 py-2 text-sm font-semibold hover:bg-amber-50"
              >
                My Tickets
              </button> */}

              <button
                type="button"
                onClick={() => nav("/listyourshow")}
                className="whitespace-nowrap rounded-full px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-amber-50 hover:text-amber-800"
              >
                List Your Show
              </button>

              {user?.token ? (
                <button
                  onClick={() => setPanelOpen(true)}
                  className="flex max-w-[220px] items-center gap-2 rounded-full border border-slate-200/90 bg-white py-1 pl-1 pr-2.5 shadow-sm transition hover:border-amber-300/80 hover:bg-amber-50/40 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 sm:pr-3"
                  aria-controls="user-panel"
                  aria-expanded={panelOpen}
                >
                  {avatarSrc ? (
                    <img
                      src={avatarSrc}
                      alt=""
                      className="h-8 w-8 shrink-0 rounded-full object-cover ring-2 ring-white"
                    />
                  ) : (
                    <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-gradient-to-br from-amber-100 to-orange-200 text-sm font-bold text-amber-950 ring-2 ring-white">
                      {getInitials(user?.name || user?.phone || "")}
                    </div>
                  )}
                  <span className="hidden min-w-0 truncate text-left text-sm font-semibold text-slate-800 select-none sm:block">
                    {user?.name || user?.phone}
                  </span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() =>
                    openAuth("login", window.location.pathname + window.location.search)
                  }
                  className="rounded-full bg-gradient-to-r from-slate-900 to-black text-white px-4 py-2 text-sm hover:opacity-95"
                >
                  Log in
                </button>
              )}
            </div>
          </div>

        </div>
      </header>

      {/* Backdrop for slide-over */}
      <div
        aria-hidden={!panelOpen}
        className={`fixed inset-0 z-40 transition-opacity ${panelOpen ? "opacity-60 pointer-events-auto" : "opacity-0 pointer-events-none"
          }`}
        style={{ backgroundColor: "rgba(0,0,0,0.5)", transition: "opacity 200ms ease" }}
        onClick={() => setPanelOpen(false)}
      />

      {/* Slide-over Panel */}
      <aside
        id="user-panel"
        ref={panelRef}
        className={`fixed top-0 right-0 z-50 flex h-full w-full max-w-[17.5rem] sm:max-w-xs flex-col bg-white font-sans shadow-2xl transition-transform duration-300 ease-in-out antialiased ${panelOpen ? "translate-x-0" : "translate-x-full"
          }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="user-panel-title"
      >
        {/* Panel header */}
        <div className="shrink-0 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-3.5 flex items-center gap-3 border-b border-white/10">
          {avatarSrc ? (
            <img
              src={avatarSrc}
              alt={user?.name || "avatar"}
              className="h-11 w-11 rounded-full object-cover border-2 border-white/90 shadow-sm"
            />
          ) : (
            <div className="h-11 w-11 rounded-full grid place-items-center bg-white/25 text-sm font-bold text-white ring-2 ring-white/40">
              {getInitials(user?.name || user?.phone || "")}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div id="user-panel-title" className="font-semibold text-sm leading-tight tracking-tight truncate">
              {user?.name || user?.phone}
            </div>
            {user?.email ? (
              <div className="text-[11px] text-amber-50/95 truncate mt-0.5">{user.email}</div>
            ) : null}
            {user?.isVerified && (
              <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-medium bg-white/20 px-1.5 py-0.5 rounded-md">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
                  <path d="M20 6L9 17l-5-5" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Verified
              </div>
            )}
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={() => setPanelOpen(false)}
            aria-label="Close user panel"
            className="shrink-0 rounded-lg p-1.5 hover:bg-white/15 transition text-white/95"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
              <path d="M6 18L18 6M6 6l12 12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        {/* Panel body — flex-1 scroll; footer stays pinned */}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain px-3 py-3 space-y-3 [scrollbar-width:thin]">
          {/* Profile strip */}
          <div className="rounded-lg border border-amber-200/80 bg-gradient-to-r from-amber-50 to-orange-50/50 px-3 py-2.5">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-wide text-amber-800/90">Member</div>
                <div className="text-xs font-semibold text-slate-800 truncate">{user?.name || "—"}</div>
              </div>
              <Link
                to="/profile"
                onClick={() => setPanelOpen(false)}
                className="shrink-0 rounded-lg bg-amber-600 text-white px-2.5 py-1.5 text-[11px] font-bold shadow-sm hover:bg-amber-700 transition"
              >
                Profile
              </Link>
            </div>
          </div>

          {/* Quick actions */}
          <div className="grid grid-cols-2 gap-2">
            <ActionTile to="/my-bookings" onClick={() => setPanelOpen(false)} icon="ticket" label="My Bookings" />
            <ActionTile to="/tickets" onClick={() => { setPanelOpen(false); onTicketsClick(); }} icon="qr" label="Show Tickets" />
            <ActionTile to="/wishlist" onClick={() => setPanelOpen(false)} icon="heart" label="Wish List" />
            {isAdmin && (
              <ActionTile to="/admin" onClick={() => setPanelOpen(false)} icon="dashboard" label="Admin Panel" />
            )}
            {isAdmin && (
              <ActionTile to="/scanner" onClick={() => setPanelOpen(false)} icon="qrscan" label="QR Scanner" />
            )}
            <ActionTile
              to="/listyourshow"
              onClick={() => setPanelOpen(false)}
              icon="listyourshow"
              label="List Your Show"
            />
            {isAdmin && (
              <ActionTile to="/layoutPage" onClick={() => setPanelOpen(false)} icon="layout" label="Layout Page" />
            )}
          </div>
        </div>

        {/* Panel footer */}
        <div className="shrink-0 border-t border-slate-200 bg-slate-50/80 px-3 py-3">
          <button
            type="button"
            onClick={handleLogout}
            className="w-full rounded-lg bg-gradient-to-r from-amber-500 to-amber-600 text-white py-2.5 text-sm font-bold shadow-sm hover:from-amber-600 hover:to-amber-700 active:scale-[0.99] transition"
          >
            Logout
          </button>
        </div>
      </aside>

      {/* 🔸 Auth Modal — choose headerVariant: "logo" OR "welcome" */}
      <AuthModal
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        defaultTab={authTab}
        next={nextAfterAuth}
        logoSrc={logo}                         // show company logo at top
        brandName="Show Time Bro"   // fallback name for welcome
        headerVariant="logo"                  // change to "welcome" for "Welcome, <name>"
      />
    </>
  );
}

/* ── Action Tile ─────────────────────────────────────────────────── */
function ActionTile({ to, onClick, icon, label }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="group rounded-lg border border-slate-200/90 bg-white hover:border-amber-300 hover:bg-amber-50/60 transition-colors flex items-center gap-2.5 px-2.5 py-2 min-h-[2.75rem]"
    >
      <div className="h-9 w-9 shrink-0 rounded-lg bg-amber-100 text-amber-800 grid place-items-center group-hover:bg-amber-200/90 transition-colors">
        <Icon kind={icon} />
      </div>
      <span className="text-left text-[11px] sm:text-xs font-semibold text-slate-800 leading-snug line-clamp-2">
        {label}
      </span>
    </Link>
  );
}

function Icon({ kind }) {
  switch (kind) {
    case "ticket":
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M4 7h16v4a2 2 0 010 4v4H4v-4a2 2 0 010-4V7z" strokeWidth="1.6" />
          <path d="M8 7v10M16 7v10" strokeWidth="1.6" />
        </svg>
      );

    case "qr":
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h2v2m2 0h2v4m-6-6v2h2" strokeWidth="1.6" />
        </svg>
      );

    /* NEW: QR Scanner (phone frame + viewfinder) */
    case "qrscan":
    case "qrScanner":
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          {/* phone frame */}
          <rect x="6" y="2.5" width="12" height="19" rx="2.5" ry="2.5" strokeWidth="1.6" />
          {/* notch / speaker */}
          <rect x="10" y="4" width="4" height="0.8" rx="0.4" stroke="none" fill="currentColor" />
          {/* viewfinder corners */}
          <path d="M9 9h2M9 9v2" strokeWidth="1.6" />
          <path d="M15 9h-2M15 9v2" strokeWidth="1.6" />
          <path d="M9 15h2M9 15v-2" strokeWidth="1.6" />
          <path d="M15 15h-2M15 15v-2" strokeWidth="1.6" />
          {/* tiny focus dot */}
          <circle cx="12" cy="12" r="0.6" fill="currentColor" stroke="none" />
        </svg>
      );

    case "heart":
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" className="text-rose-500" fill="none" stroke="currentColor">
          <path d="M4.5 7a4.5 4.5 0 016.364 0L12 8.136 13.136 7a4.5 4.5 0 116.364 6.364L12 21l-7.5-7.636A4.5 4.5 0 014.5 7z" strokeWidth="1.6" />
        </svg>
      );

    case "settings":
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path
            d="M12 15.5A3.5 3.5 0 1112 8.5a3.5 3.5 0 010 7zM19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09a1.65 1.65 0 00-1 1.51 1.65 1.65 0 00-1.82.33l-.06.06A2 2 0 112.3 16.9l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82L4.3 4.3A2 2 0 116.9 2.3l.06.06a1.65 1.65 0 001.82.33H9c.61.36 1 .9 1 1.51V4a2 2 0 114 0v.09c0 .61 1.18 1.51 1.82 1.51h.02"
            strokeWidth="1.2"
          />
        </svg>
      );
    case "listyourshow":
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          {/* Ticket outline */}
          <path
            d="M4 7h16v3a2 2 0 010 4v3H4v-3a2 2 0 010-4V7z"
            strokeWidth="1.6"
          />
          {/* Divider lines */}
          <path d="M8 7v10M16 7v10" strokeWidth="1.6" />
          {/* Plus symbol (add show) */}
          <circle cx="12" cy="12" r="0.1" fill="none" stroke="none" />
          <path d="M12 5v4M10 7h4" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );


    case "dashboard":
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M3 7h18M3 7v13a1 1 0 001 1h16a1 1 0 001-1V7M8 3v4M16 3v4" strokeWidth="1.6" />
        </svg>
      );
    case "layout":
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <rect x="3" y="3" width="18" height="4" rx="1" strokeWidth="1.6" />
          <rect x="3" y="9" width="8" height="12" rx="1" strokeWidth="1.6" />
          <rect x="13" y="9" width="8" height="12" rx="1" strokeWidth="1.6" />
        </svg>
      );

    case "help":
    default:
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M9 9a3 3 0 116 0c0 3-3 3-3 6" strokeWidth="1.6" />
          <circle cx="12" cy="18" r="1" />
        </svg>
      );
  }
}

